import { useEffect, useState } from "react";
import { ShieldAlert, ChevronDown, ChevronUp, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Production health banner — ADMIN ONLY.
 *
 * Pings /api/health/full once per session and surfaces an amber/red banner
 * when MongoDB / APP_URL / JWT_SECRET / Resend / Stripe is misconfigured.
 * Designed to catch the canonical post-redeploy env-var-missing recurrence.
 *
 * Renders nothing for non-admin users (landlords, tenants), nothing when
 * the data isn't loaded yet, nothing when severity is `ok`, and nothing
 * after the user dismisses it for the session.
 *
 * Cached in sessionStorage so we don't re-ping on every navigation.
 */
export default function HealthBanner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem("rv_health_banner_dismissed") === "1";
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin || dismissed) return;
    const cached = sessionStorage.getItem("rv_health_full");
    if (cached) {
      try {
        setData(JSON.parse(cached));
        return;
      } catch {
        /* ignore */
      }
    }
    api
      .get("/health/full")
      .then((r) => {
        setData(r.data);
        try {
          sessionStorage.setItem("rv_health_full", JSON.stringify(r.data));
        } catch {
          /* quota — ignore */
        }
      })
      .catch(() => {
        /* silent — health check is best-effort */
      });
  }, [isAdmin, dismissed]);

  if (!isAdmin || dismissed || !data || data.severity === "ok") return null;

  const isError = data.severity === "error";
  const tone = isError
    ? "border-red-300 bg-red-50 text-red-900"
    : "border-amber-300 bg-amber-50 text-amber-900";
  const iconTone = isError ? "text-red-600" : "text-amber-600";
  const failing = (data.checks || []).filter((c) => c.status !== "ok");

  const dismiss = () => {
    sessionStorage.setItem("rv_health_banner_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className={`mb-6 border ${tone} rounded-sm p-4`}
      data-testid="health-banner"
      data-severity={data.severity}
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className={`w-5 h-5 ${iconTone} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">
            {isError ? "System health: critical" : "System health: configuration warning"}
          </div>
          <div className="text-xs mt-0.5 opacity-90">
            {failing.length} of {data.checks.length} checks failing — some features may not work as expected.
          </div>

          {expanded && (
            <ul className="mt-3 space-y-1.5 text-xs" data-testid="health-banner-details">
              {data.checks.map((c) => (
                <li
                  key={c.name}
                  className="flex items-start gap-2"
                  data-testid={`health-check-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span
                    className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      c.status === "ok"
                        ? "bg-emerald-500"
                        : c.status === "warn"
                        ? "bg-amber-500"
                        : "bg-red-500"
                    }`}
                  />
                  <span className="font-mono font-semibold mr-1">{c.name}:</span>
                  <span className="opacity-80">{c.detail}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold underline-offset-2 hover:underline"
              data-testid="health-banner-toggle"
            >
              {expanded ? (
                <>
                  Hide details <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  Show details <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="opacity-60 hover:opacity-100 p-1 -mt-1 -mr-1"
          title="Dismiss for this session"
          aria-label="Dismiss health banner"
          data-testid="health-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
